import { create } from 'zustand';
import type {
  EditorBook,
  EditorPage,
  EditorElement,
  EditorState,
  Keyframe,
  StoryText,
  Vec3Tuple,
} from '../types';

let uid = 0;
const genId = (prefix: string) => `${prefix}_${Date.now().toString(36)}_${uid++}`;

function createEmptyPage(index: number): EditorPage {
  return {
    id: genId('page'),
    title: `第 ${index + 1} 页`,
    background: { color: '#1a2340', sceneType: 'story_page', timeOfDay: 'day', mood: 'peaceful' },
    lighting: { preset: 'soft-glow', intensity: 1.0, fog: true },
    particles: { preset: 'sparkles', count: 80, enabled: false },
    elements: [],
    texts: [],
    duration: 8,
  };
}

function createEmptyBook(): EditorBook {
  const now = new Date().toISOString();
  const page = createEmptyPage(0);
  return {
    id: genId('book'),
    title: '未命名绘本',
    cover: '',
    author: '',
    language: 'zh',
    created: now,
    updated: now,
    pages: [page],
  };
}

interface EditorStore extends EditorState {
  createBook: () => void;
  loadBook: (book: EditorBook) => void;
  addPage: () => void;
  deletePage: (pageId: string) => void;
  setCurrentPage: (pageId: string) => void;
  getCurrentPage: () => EditorPage | null;
  addElement: (category: string, presetName: string) => void;
  updateElement: (elementId: string, patch: Partial<EditorElement>) => void;
  updateElementPosition: (elementId: string, position: Vec3Tuple) => void;
  updateElementRotation: (elementId: string, rotation: Vec3Tuple) => void;
  updateElementScale: (elementId: string, scale: Vec3Tuple) => void;
  updateElementNarrative: (elementId: string, narrative: EditorElement['narrative']) => void;
  updateMetadata: (pageId: string, field: 'lighting' | 'particles' | 'background', value: unknown) => void;
  importPipelineResult: (pipelineJson: any) => void;
  loadAIGeneratedBook: (aiPages: Array<{page_number: number; title: string; content: string; image_path?: string}>, title: string) => void;
  publishBook: () => Promise<{success: boolean; slug?: string; error?: string}>;
  setPageDuration: (pageId: string, duration: number) => void;
  removeElement: (elementId: string) => void;
  addKeyframe: (elementId: string, keyframe: Omit<Keyframe, 'id'>) => void;
  removeKeyframe: (elementId: string, keyframeId: string) => void;
  addText: (content?: string) => void;
  updateText: (textId: string, patch: Partial<StoryText>) => void;
  removeText: (textId: string) => void;
  setPlaying: (playing: boolean) => void;
  setPlayhead: (time: number) => void;
  setSelectedElement: (elementId: string | null) => void;
  undo: () => void;
  redo: () => void;
  addHistory: (label: string) => void;
  resetEditor: () => void;
}

export const editorStore = create<EditorStore>((set, get) => ({
  book: null,
  currentPageId: null,
  selectedElementId: null,
  playing: false,
  playhead: 0,
  undoStack: [],
  redoStack: [],

  createBook: () => {
    const book = createEmptyBook();
    set({
      book,
      currentPageId: book.pages[0].id,
      undoStack: [],
      redoStack: [],
    });
  },

  loadBook: (book) => {
    set({
      book,
      currentPageId: book.pages[0]?.id ?? null,
      selectedElementId: null,
      undoStack: [{ book: structuredClone(book), label: '载入', timestamp: Date.now() }],
      redoStack: [],
    });
  },

  addPage: () => {
    const { book } = get();
    if (!book) return;
    const page = createEmptyPage(book.pages.length);
    const next = { ...book, pages: [...book.pages, page], updated: new Date().toISOString() };
    set({ book: next, currentPageId: page.id, playhead: 0 });
    get().addHistory('新增页面');
  },

  deletePage: (pageId) => {
    const { book, currentPageId } = get();
    if (!book || book.pages.length <= 1) return;
    const pages = book.pages.filter((p) => p.id !== pageId);
    const next = { ...book, pages, updated: new Date().toISOString() };
    set({
      book: next,
      currentPageId: currentPageId === pageId ? pages[0].id : currentPageId,
    });
    get().addHistory('删除页面');
  },

  setCurrentPage: (pageId) => set({ currentPageId: pageId, selectedElementId: null, playhead: 0 }),

  getCurrentPage: () => {
    const { book, currentPageId } = get();
    if (!book || !currentPageId) return null;
    return book.pages.find((p) => p.id === currentPageId) ?? null;
  },

  addElement: (category, presetName) => {
    const page = get().getCurrentPage();
    if (!page) return;
    const el: EditorElement = {
      id: genId('el'),
      name: presetName || category,
      category: category as EditorElement['category'],
      position: { x: 0, y: 0, z: 0.4 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
      visible: true,
      opacity: 1,
      narrative: {
        role: category === 'character' ? 'main_character' : 'prop',
        emotion: 'calm',
        texture: 'soft',
        physics: 'rigid',
        behavior: 'idle',
        intensity: 0.5,
        interaction: 'none',
      },
      keyframes: [],
    };
    const next = {
      ...get().book!,
      pages: get().book!.pages.map((p) =>
        p.id === page.id ? { ...p, elements: [...p.elements, el] } : p
      ),
      updated: new Date().toISOString(),
    };
    set({ book: next, selectedElementId: el.id });
    get().addHistory('添加元素');
  },

  updateElement: (elementId, patch) => {
    set((state) => {
      if (!state.book) return state;
      return {
        book: {
          ...state.book,
          pages: state.book.pages.map((p) => ({
            ...p,
            elements: p.elements.map((e) =>
              e.id === elementId ? { ...e, ...patch } : e
            ),
          })),
        },
      };
    });
  },

  updateElementPosition: (elementId, position) => {
    get().updateElement(elementId, { position });
  },
  updateElementRotation: (elementId, rotation) => {
    get().updateElement(elementId, { rotation });
  },
  updateElementScale: (elementId, scale) => {
    get().updateElement(elementId, { scale });
  },

  updateElementNarrative: (elementId, narrative) => {
    get().updateElement(elementId, { narrative });
  },

  updateMetadata: (pageId, field, value) => {
    set((state) => {
      if (!state.book) return state;
      return {
        book: {
          ...state.book,
          pages: state.book.pages.map((p) =>
            p.id === pageId ? { ...p, [field]: { ...(p as any)[field], ...(value as any) } } : p
          ),
        },
      };
    });
  },

  setPageDuration: (pageId, duration) => {
    set((state) => ({
      book: state.book
        ? {
            ...state.book,
            pages: state.book.pages.map((p) => (p.id === pageId ? { ...p, duration } : p)),
          }
        : null,
    }));
  },

  removeElement: (elementId) => {
    const page = get().getCurrentPage();
    if (!page) return;
    set((state) => ({
      book: state.book
        ? {
            ...state.book,
            pages: state.book.pages.map((p) =>
              p.id === page.id
                ? { ...p, elements: p.elements.filter((e) => e.id !== elementId) }
                : p
            ),
          }
        : null,
      selectedElementId: null,
    }));
    get().addHistory('删除元素');
  },

  addKeyframe: (elementId, keyframe) => {
    const kf: Keyframe = { ...keyframe, id: genId('kf') };
    set((state) => ({
      book: state.book
        ? {
            ...state.book,
            pages: state.book.pages.map((p) => ({
              ...p,
              elements: p.elements.map((e) =>
                e.id === elementId
                  ? { ...e, keyframes: [...(e.keyframes ?? []), kf].sort((a, b) => a.time - b.time) }
                  : e
              ),
            })),
          }
        : null,
    }));
    get().addHistory('添加关键帧');
  },

  removeKeyframe: (elementId, keyframeId) => {
    set((state) => ({
      book: state.book
        ? {
            ...state.book,
            pages: state.book.pages.map((p) => ({
              ...p,
              elements: p.elements.map((e) =>
                e.id === elementId
                  ? { ...e, keyframes: (e.keyframes ?? []).filter((k) => k.id !== keyframeId) }
                  : e
              ),
            })),
          }
        : null,
    }));
    get().addHistory('删除关键帧');
  },

  addText: (content = '旁白文本，支持逐字高亮朗读') => {
    const page = get().getCurrentPage();
    if (!page) return;
    const text: StoryText = {
      id: genId('txt'),
      content,
      language: get().book?.language ?? 'zh',
      position: { x: 0, y: -2.2, z: 0.6 },
      fontSize: 28,
      color: '#ffffff',
      bold: false,
      align: 'center',
    };
    set((state) => ({
      book: state.book
        ? {
            ...state.book,
            pages: state.book.pages.map((p) =>
              p.id === page.id ? { ...p, texts: [...p.texts, text] } : p
            ),
          }
        : null,
    }));
    get().addHistory('添加文本');
  },

  updateText: (textId, patch) => {
    set((state) => ({
      book: state.book
        ? {
            ...state.book,
            pages: state.book.pages.map((p) => ({
              ...p,
              texts: p.texts.map((t) => (t.id === textId ? { ...t, ...patch } : t)),
            })),
          }
        : null,
    }));
  },

  removeText: (textId) => {
    set((state) => ({
      book: state.book
        ? {
            ...state.book,
            pages: state.book.pages.map((p) => ({
              ...p,
              texts: p.texts.filter((t) => t.id !== textId),
            })),
          }
        : null,
    }));
    get().addHistory('删除文本');
  },

  setPlaying: (playing) => set({ playing }),
  setPlayhead: (playhead) => set({ playhead }),
  setSelectedElement: (selectedElementId) => set({ selectedElementId }),

  addHistory: (label: string) => {
    const { book, undoStack } = get();
    if (!book) return;
    set({
      undoStack: [
        ...undoStack.slice(-49),
        { book: structuredClone(book), label, timestamp: Date.now() },
      ],
      redoStack: [],
    });
  },

  undo: () => {
    const { undoStack, redoStack, book } = get();
    if (!undoStack.length || !book) return;
    const prev = undoStack[undoStack.length - 1];
    set({
      book: structuredClone(prev.book),
      undoStack: undoStack.slice(0, -1),
      redoStack: [...redoStack, { book: structuredClone(book), label: '撤销', timestamp: Date.now() }],
      selectedElementId: null,
    });
  },

  redo: () => {
    const { redoStack, undoStack, book } = get();
    if (!redoStack.length || !book) return;
    const next = redoStack[redoStack.length - 1];
    set({
      book: structuredClone(next.book),
      redoStack: redoStack.slice(0, -1),
      undoStack: [...undoStack, { book: structuredClone(book), label: '重做', timestamp: Date.now() }],
      selectedElementId: null,
    });
  },

  resetEditor: () => set({
    book: null,
    currentPageId: null,
    selectedElementId: null,
    playing: false,
    playhead: 0,
    undoStack: [],
    redoStack: [],
  }),

  importPipelineResult: (pipelineJson) => {
    const { book, currentPageId } = get();
    if (!book || !currentPageId) return;

    const data = pipelineJson ?? {};
    const layers = Array.isArray(data.layers) ? data.layers : [];
    const narration = typeof data.narration === 'string' ? data.narration : '';
    const scene = data.scene_analysis ?? {};

    // 1. 将 layers 转换为 EditorElement
    const importedElements: EditorElement[] = layers.map((layer: any) => {
      const cat = String(layer.category ?? 'prop').toLowerCase();
      const category = ['character', 'prop', 'background', 'decoration'].includes(cat)
        ? (cat as EditorElement['category'])
        : 'prop';

      return {
        id: genId('el'),
        name: String(layer.id ?? 'imported_layer'),
        category,
        position: {
          x: Number(layer.bbox?.x ?? 0),
          y: Number(layer.bbox?.y ?? 0),
          z: Number(layer.bbox?.z ?? 0.4),
        },
        rotation: { x: 0, y: 0, z: 0 },
        scale: {
          x: Number(layer.bbox?.width ?? 1),
          y: Number(layer.bbox?.height ?? 1),
          z: 1,
        },
        visible: true,
        opacity: 1,
        narrative: {
          role: String(layer.category ?? 'prop'),
          emotion: String(layer.emotion ?? 'calm'),
          texture: String(layer.texture ?? 'soft'),
          physics: String(layer.physics ?? 'rigid'),
          behavior: 'idle',
          intensity: 0.5,
          interaction: String(layer.interaction ?? layer.interaction?.type ?? 'none'),
        },
        keyframes: [],
      };
    });

    // 2. 将 narration 转换为 StoryText
    const importedTexts: StoryText[] = narration
      ? [
          {
            id: genId('txt'),
            content: narration,
            language: book.language ?? 'zh',
            position: { x: 0, y: -2.2, z: 0.6 },
            fontSize: 28,
            color: '#ffffff',
            bold: false,
            align: 'center',
          },
        ]
      : [];

    // 3. 将 scene_analysis 转换为页面 lighting/particles/background 配置
    const lighting = scene.lighting ?? {};
    const particles = scene.particles ?? {};

    const nextPages = book.pages.map((p) => {
      if (p.id !== currentPageId) return p;

      return {
        ...p,
        background: {
          ...p.background,
          sceneType: String(scene.scene_type ?? p.background.sceneType),
          mood: String(scene.mood ?? p.background.mood),
        },
        lighting: {
          ...p.lighting,
          preset: String(lighting.preset ?? p.lighting.preset),
          intensity: Number(lighting.intensity ?? p.lighting.intensity),
        },
        particles: {
          ...p.particles,
          preset: String(particles.preset ?? p.particles.preset),
          count: Number(particles.count ?? p.particles.count),
          enabled: Boolean(particles.enabled ?? p.particles.enabled),
        },
        elements: [...p.elements, ...importedElements],
        texts: [...p.texts, ...importedTexts],
      };
    });

    const next = {
      ...book,
      pages: nextPages,
      updated: new Date().toISOString(),
    };

    set({ book: next });
    get().addHistory('导入 AI 分层结果');
  },

  loadAIGeneratedBook: (aiPages, title) => {
    const now = new Date().toISOString();
    const mappedPages: EditorPage[] = aiPages.map((p) => ({
      id: genId('page'),
      title: p.title || `第 ${p.page_number} 页`,
      background: {
        color: '#1a2340',
        sceneType: 'story_page',
        timeOfDay: 'day',
        mood: 'peaceful',
        imageUrl: p.image_path,
      },
      lighting: { preset: 'soft-glow', intensity: 1.0, fog: true },
      particles: { preset: 'sparkles', count: 80, enabled: false },
      elements: [],
      texts: [
        {
          id: genId('txt'),
          content: p.content || '',
          language: 'zh',
          position: { x: 0, y: -2.2, z: 0.6 },
          fontSize: 28,
          color: '#ffffff',
          bold: false,
          align: 'center',
        },
      ],
      duration: 8,
    }));

    const book: EditorBook = {
      id: genId('book'),
      title: title || 'AI 生成的绘本',
      cover: aiPages[0]?.image_path || '',
      author: 'AI Studio',
      language: 'zh',
      created: now,
      updated: now,
      pages: mappedPages.length > 0 ? mappedPages : [createEmptyPage(0)],
    };

    set({ book, currentPageId: book.pages[0]?.id ?? null });
    get().addHistory('加载 AI 生成绘本');
  },

  publishBook: async () => {
    const { book } = get();
    if (!book) return { success: false, error: '没有可发布的绘本' };
    try {
      const res = await fetch(
        (import.meta.env.VITE_API_BASE_URL || '/api') + '/admin/books',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          },
          body: JSON.stringify({
            title: book.title,
            author: book.author,
            language: book.language,
            pages: book.pages.map((p, i) => ({
              page_number: i + 1,
              title: p.title,
              content: p.texts[0]?.content || '',
              scene_3d: {
                background: p.background,
                lighting: p.lighting,
                elements: p.elements,
              },
              duration_seconds: p.duration,
            })),
          }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return { success: true, slug: data.slug };
    } catch (err: any) {
      return { success: false, error: err.message || '发布失败' };
    }
  },
}));

export function useEditor() {
  return editorStore;
}